import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export const generateId = () => {
  return Math.random().toString(36).substring(2, 15);
};

export const formatDate = (dateString) => {
  if (!dateString) return "Present";

  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
  });
};

export const downloadPdf = (elementId, fileName) => {
  const element = document.getElementById(elementId);
  if (!element) return;

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow popups for this website");
    return;
  }

  const printDocument = printWindow.document;

  // Inject Tailwind CDN
  const tailwind = printDocument.createElement("link");
  tailwind.href =
    "https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css";
  tailwind.rel = "stylesheet";
  printDocument.head.appendChild(tailwind);

  // Inject custom print styles
  const style = printDocument.createElement("style");
  style.innerHTML = `
    @page {
      size: A4;
      margin: 2mm;
    }

    body {
      font-family: 'Inter', sans-serif;
      line-height: 1.4;
    }
     
    * {
      box-sizing: border-box;
    }
  `;
  printDocument.head.appendChild(style);

  // Add Google Fonts
  const font = printDocument.createElement("link");
  font.href =
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
  font.rel = "stylesheet";
  printDocument.head.appendChild(font);

  // Set the body content with correct font size
  printDocument.body.innerHTML = `<div class="text-[10px] font-sans">${element.outerHTML}</div>`;

  setTimeout(() => {
    printWindow.document.title = fileName;
    printWindow.print();
  }, 500);
};

// A4 page dimensions in pixels (assuming 96 DPI)
export const A4_DIMENSIONS = {
  width: 794, // 210mm
  height: 1123, // 297mm
  margins: {
    top: 20,
    bottom: 20,
    left: 20,
    right: 20,
  },
};

// Enhanced page overflow detection
export const checkPageOverflow = (contentElement) => {
  if (!contentElement) return { hasOverflow: false, contentHeight: 0 };

  const contentHeight = contentElement.scrollHeight;
  const maxHeight =
    A4_DIMENSIONS.height -
    (A4_DIMENSIONS.margins.top + A4_DIMENSIONS.margins.bottom);
  const overflowAmount = Math.max(0, contentHeight - maxHeight);

  // Calculate approximate number of lines based on current line height
  const computedStyle = window.getComputedStyle(contentElement);
  const lineHeight =
    parseFloat(computedStyle.lineHeight) ||
    parseFloat(computedStyle.fontSize) * 1.2;
  const approximateLines = Math.ceil(overflowAmount / lineHeight);

  return {
    hasOverflow: contentHeight > maxHeight,
    contentHeight,
    maxHeight,
    overflowAmount,
    approximateLines,
    lineHeight,
  };
};

// Enhanced font size adjustment with better readability preservation
export const adjustFontSize = (element, maxHeight) => {
  if (!element) return null;

  const originalStyle = window.getComputedStyle(element);
  const originalFontSize = parseFloat(originalStyle.fontSize);
  let currentFontSize = originalFontSize;
  const minFontSize = 9; // Minimum readable font size

  // Store original font sizes of all child elements
  const childElements = element.getElementsByTagName("*");
  const originalSizes = new Map();
  Array.from(childElements).forEach((el) => {
    originalSizes.set(el, parseFloat(window.getComputedStyle(el).fontSize));
  });

  // Adjust font sizes proportionally
  const adjustElements = (scaleFactor) => {
    Array.from(childElements).forEach((el) => {
      const originalSize = originalSizes.get(el);
      const newSize = Math.max(originalSize * scaleFactor, minFontSize);
      el.style.fontSize = `${newSize}px`;
    });
    element.style.fontSize = `${currentFontSize}px`;
  };

  // Binary search for optimal font size
  let low = minFontSize;
  let high = originalFontSize;
  let bestFit = null;

  while (low <= high) {
    currentFontSize = (low + high) / 2;
    const scaleFactor = currentFontSize / originalFontSize;
    adjustElements(scaleFactor);

    if (element.scrollHeight <= maxHeight) {
      bestFit = currentFontSize;
      low = currentFontSize + 0.5;
    } else {
      high = currentFontSize - 0.5;
    }
  }

  // Apply best fit if found, otherwise use minimum size
  if (bestFit) {
    currentFontSize = bestFit;
    adjustElements(bestFit / originalFontSize);
  } else {
    currentFontSize = minFontSize;
    adjustElements(minFontSize / originalFontSize);
  }

  return {
    originalFontSize,
    adjustedFontSize: currentFontSize,
    reductionPercentage:
      ((originalFontSize - currentFontSize) / originalFontSize) * 100,
  };
};

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}
